import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

function EventSpaceDetail() {
  const { id } = useParams();
  const [eventSpace, setEventSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [comments, setComments] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchEventSpace = async () => {
      try {
        const eventSpaceDocRef = doc(db, 'eventSpaces', id);
        const eventSpaceDocSnap = await getDoc(eventSpaceDocRef);

        if (eventSpaceDocSnap.exists()) {
          const data = { id: eventSpaceDocSnap.id, ...eventSpaceDocSnap.data() };
          setEventSpace(data);
          setServices(data.services || []); // Set services directly from eventSpace
        } else {
          setError('No se encontró el salón.');
        }
      } catch (err) {
        setError('Error al cargar los detalles del salón.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchPhotos = async () => {
      try {
        const photosCollectionRef = collection(db, 'eventSpaces', id, 'photos');
        const photosSnapshot = await getDocs(photosCollectionRef);
        setPhotos(photosSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      } catch (err) {
        console.error('Error al cargar las fotos:', err);
      }
    };

    fetchEventSpace();
    fetchPhotos();
  }, [id]);

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService || !acceptTerms) {
      setFormMessage('Por favor, selecciona un servicio y acepta los términos y condiciones.');
      return;
    }

    try {
      await addDoc(collection(db, 'reservations'), {
        eventSpaceId: id,
        eventSpaceTitle: eventSpace.title,
        serviceId: selectedService,
        comments,
        timestamp: new Date(),
      });
      setFormMessage('¡Reserva enviada con éxito!');
      // Clear form fields
      setSelectedService('');
      setComments('');
      setAcceptTerms(false);
    } catch (err) {
      setFormMessage('Error al enviar la reserva.');
      console.error(err);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedFile) {
      setFormMessage('Por favor, selecciona una imagen para subir.');
      return;
    }

    try {
      const imageRef = ref(storage, `salones/${id}/${selectedFile.name}`);
      await uploadBytes(imageRef, selectedFile);
      const imageUrl = await getDownloadURL(imageRef);

      const photosCollectionRef = collection(db, 'eventSpaces', id, 'photos');
      const docRef = await addDoc(photosCollectionRef, { imageUrl });

      setPhotos([...photos, { id: docRef.id, imageUrl }]);
      setSelectedFile(null);
      setFormMessage('¡Foto subida con éxito!');
    } catch (err) {
      setFormMessage('Error al subir la foto.');
      console.error(err);
    }
  };

  const getServiceDetails = () => {
    const service = services.find(s => s.id === selectedService);
    return service ? (
      <div>
        <p><strong>Descripción del Servicio:</strong> {service.description}</p>
        <p><strong>Valor:</strong> ${service.price}</p>
      </div>
    ) : null;
  };

  if (loading) {
    return <p>Cargando detalles del salón...</p>;
  }

  if (error) {
    return <p>{error}</p>;
  }

  if (!eventSpace) {
    return <p>Salón no disponible.</p>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>{eventSpace.title}</h2>
      <img src={eventSpace.imageUrl} alt={eventSpace.title} style={{ maxWidth: '100%', height: 'auto' }} />
      <p>{eventSpace.description}</p>

      <div style={{ marginTop: '20px' }}>
        <h3>Fotos del Salón</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {photos.map(photo => (
            <img key={photo.id} src={photo.imageUrl} alt="Foto del salón" style={{ width: '200px', height: '150px', objectFit: 'cover' }} />
          ))}
        </div>
        <div style={{ marginTop: '10px' }}>
          <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} />
          <button onClick={handlePhotoUpload}>Subir Foto</button>
        </div>
      </div>

      <h3>Realizar Reserva</h3>
      <form onSubmit={handleReservationSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '500px', margin: '20px auto 0' }}>
        <div>
          <label htmlFor="service">Servicio:</label>
          <select id="service" value={selectedService} onChange={(e) => setSelectedService(e.target.value)} required style={{ width: '100%', padding: '8px' }}>
            <option value="">Selecciona un servicio</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>{service.name}</option>
            ))}
          </select>
          {selectedService && getServiceDetails()}
        </div>

        <div>
          <label htmlFor="comments">Comentarios (opcional):</label>
          <textarea id="comments" value={comments} onChange={(e) => setComments(e.target.value)} rows="4" style={{ width: '100%', padding: '8px' }}></textarea>
        </div>

        <div>
          <input type="checkbox" id="acceptTerms" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required />
          <label htmlFor="acceptTerms">Acepto los términos y condiciones.</label>
          <div style={{ border: '1px solid #ccc', padding: '10px', marginTop: '10px', maxHeight: '150px', overflowY: 'auto' }}>
            <h4>Términos y Condiciones de Reserva</h4>
            <p>1. La reserva se considera confirmada una vez recibido el pago o la señal acordada.</p>
            <p>2. Las cancelaciones realizadas con menos de 48 horas de antelación pueden incurrir en cargos.</p>
            <p>3. Cualquier daño causado a las instalaciones será responsabilidad del cliente.</p>
            <p>4. Nos reservamos el derecho de admisión.</p>
            <p>5. Se puntual, ya que solo garantizamos la reserva por 15 minutos después de la hora acordada.</p>
            <p>6. Por favor, asegúrese de leer y entender todas las condiciones antes de confirmar su reserva.</p>
            <p>7. Para cualquier modificación o consulta, por favor, contáctenos directamente.</p>
          </div>
        </div>

        {formMessage && <p style={{ marginTop: '10px', color: formMessage.includes('Error') ? 'red' : 'green' }}>{formMessage}</p>}

        <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Enviar Reserva</button>
      </form>
    </div>
  );
}

export default EventSpaceDetail;
